import { Button, Col, Form, Input, Row, Card, message } from 'antd';
import TextArea from 'antd/lib/input/TextArea';
import { UpOutlined, DownOutlined } from '@ant-design/icons';
import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import styled from '@emotion/styled';
import { GameProfileItem } from '../../../types/gameProfile';
import { createOrUpdateGameProfileQuestionnaireRepository } from '../../../repository/mytm/gameProfile/gameProfileRepository';
import { logging } from '../../../utils/log';
import { GAME_PROFILE } from '../../../constants/define/translation';
import { appStyles } from '../../../styles';

interface QuestionnaireProps {
  data?: GameProfileItem | null;
  onSave?: (data: GameProfileItem) => void;
  country?: string | null;
}

const StyledFormContainer = styled.div`
  & textarea {
    ${appStyles.commonBorderRadius}
  }
`;

export const QuestionnaireEnhanced: React.FC<QuestionnaireProps> = ({ data, onSave, country }) => {
  const { t } = useTranslation(GAME_PROFILE);
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const [isExpanded, setIsExpanded] = useState(false);

  const handleSave = async (values: any) => {
    try {
      setLoading(true);
      const formData = {
        header: values.header,
        subHeader: values.subHeader,
        thankYouHeader: values.thankYouHeader,
        thankYouMessage: values.thankYouMessage,
        options: {
          headerColor: values.headerColor || '#7b7b7b',
          subHeaderColor: values.subHeaderColor || '#127603',
        },
        country: country || undefined,
      };

      const result = await createOrUpdateGameProfileQuestionnaireRepository(formData);

      onSave?.(result);
      message.success(t('questionnaire.questionSuccess'));
    } catch (error) {
      logging(error);
      message.error(t('questionnaire.questionFailed'));
    } finally {
      setLoading(false);
    }
  };

  const toggleExpanded = () => {
    setIsExpanded(!isExpanded);
  };

  return (
    <Card style={{ marginBottom: 24 }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
        <h3 style={{ margin: 0, fontSize: 18, fontWeight: 'bold' }}>{t('questionnaire.title')}</h3>
        <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
          <Button
            type="text"
            icon={isExpanded ? <UpOutlined /> : <DownOutlined />}
            onClick={toggleExpanded}
            size="small"
          />
        </div>
      </div>

      {isExpanded && (
        <StyledFormContainer>
          <Form
            form={form}
            layout="vertical"
            initialValues={{
              header: data?.header || '',
              subHeader: data?.subHeader || '',
              thankYouHeader: data?.thankYouHeader || '',
              thankYouMessage: data?.thankYouMessage || '',
              headerColor: data?.options ? JSON.parse(data.options).headerColor : '#7b7b7b',
              subHeaderColor: data?.options ? JSON.parse(data.options).subHeaderColor : '#127603',
            }}
            onFinish={handleSave}
          >
            <Row gutter={24}>
              <Col span={12}>
                <Form.Item
                  name="header"
                  label={<span style={{ fontWeight: 'bold' }}>{t('questionnaire.header')}</span>}
                  rules={[{ required: true, message: t('questionnaire.validate.header') }]}
                >
                  <Input
                    placeholder={t('questionnaire.placeholder.header')}
                    maxLength={255}
                    style={{ borderRadius: 6 }}
                  />
                </Form.Item>

                <Form.Item
                  name="headerColor"
                  label={<span style={{ fontWeight: 'bold' }}>{t('questionnaire.headerColor')}</span>}
                >
                  <Input placeholder={t('questionnaire.placeholder.headerColor')} style={{ borderRadius: 6 }} />
                </Form.Item>

                <Form.Item
                  name="thankYouHeader"
                  label={<span style={{ fontWeight: 'bold' }}>{t('questionnaire.thankYouHeader')}</span>}
                  rules={[{ required: true, message: t('questionnaire.validate.thankYouHeader') }]}
                >
                  <Input
                    placeholder={t('questionnaire.placeholder.thankYouHeader')}
                    maxLength={255}
                    style={{ borderRadius: 6 }}
                  />
                </Form.Item>
              </Col>

              <Col span={12}>
                <Form.Item
                  name="subHeader"
                  label={<span style={{ fontWeight: 'bold' }}>{t('questionnaire.subHeader')}</span>}
                >
                  <Input
                    placeholder={t('questionnaire.placeholder.subHeader')}
                    maxLength={255}
                    style={{ borderRadius: 6 }}
                  />
                </Form.Item>

                <Form.Item
                  name="subHeaderColor"
                  label={<span style={{ fontWeight: 'bold' }}>{t('questionnaire.subHeaderColor')}</span>}
                >
                  <Input placeholder={t('questionnaire.placeholder.subHeaderColor')} style={{ borderRadius: 6 }} />
                </Form.Item>

                <Form.Item
                  name="thankYouMessage"
                  label={<span style={{ fontWeight: 'bold' }}>{t('questionnaire.thankYouMessage')}</span>}
                  rules={[{ required: true, message: t('questionnaire.validate.thankYouMessage') }]}
                >
                  <TextArea
                    rows={4}
                    placeholder={t('questionnaire.placeholder.thankYouMessage')}
                    showCount
                    style={{
                      resize: 'none',
                    }}
                  />
                </Form.Item>
              </Col>
            </Row>

            <div style={{ textAlign: 'right', marginTop: 16 }}>
              <Button
                type="primary"
                htmlType="submit"
                loading={loading}
                style={{ backgroundColor: '#000', borderColor: '#000', borderRadius: 6, height: 44 }}
              >
                {t('questionnaire.saveButton')}
              </Button>
            </div>
          </Form>
        </StyledFormContainer>
      )}
    </Card>
  );
};
